/*
 * Gift ladder rendering.
 *
 * Reads nothing and calculates nothing: every number arrives on the
 * `cp:ladder` event from cp-ladder-ui.js, which got it from computeLadder().
 * This file only moves the bar, flips the chips and decides whether the gift
 * picker is on screen.
 *
 * Everything here is delegated from `document` and re-queried on each event.
 * That is deliberate: the cart drawer replaces its own markup after every
 * mutation and the dev server swaps whole sections on hot reload, so any
 * listener bound to a particular node dies the first time that happens — which
 * is exactly how the "Рівні" button ended up doing nothing.
 */
import { CP_LADDER_EVENT, formatMoney, enqueue, announce, track } from '@theme/cp-ladder-ui';

function sentence(root, state) {
  const amount = formatMoney(state.toNext);

  // Named by the next level that exists, not by which flags are set: with
  // delivery switched off, "not T1" no longer means "delivery is next".
  switch (state.nextKind) {
    case 'shipping': return root.dataset.copyShipping.replace('%amount%', amount);
    case 'gift': return root.dataset.copyGift.replace('%amount%', amount);
    case 'cheapest': return root.dataset.copyCheapest.replace('%amount%', amount);
    default: return root.dataset.copyAll;
  }
}

function itemsWord(n) {
  const m100 = n % 100;
  const m10 = n % 10;
  if (m100 >= 11 && m100 <= 14) return 'речей';
  if (m10 === 1) return 'річ';
  if (m10 >= 2 && m10 <= 4) return 'речі';
  return 'речей';
}

/* Free delivery by item count — Chekly's rule, mirrored (see the snippet). */
function paintShipping(root, cart) {
  const card = root.querySelector('[data-cp-ladder-ship]');
  if (!card || !cart) return;
  const need = Number(card.dataset.items) || 0;
  const have = (cart.items || []).reduce(function (sum, item) {
    return item.properties && item.properties._gift === 'true' ? sum : sum + item.quantity;
  }, 0);
  const done = have >= need;
  const wasOn = card.classList.contains('is-on');
  card.classList.toggle('is-on', done);
  const status = card.querySelector('[data-cp-goal-status]');
  if (status) status.textContent = done ? 'Ваша' : 'ще ' + (need - have) + ' ' + itemsWord(need - have);
  card.querySelectorAll('.cp-goal__segs i').forEach(function (seg, i) { seg.classList.toggle('is-on', i < have); });
  if (done && !wasOn && window.cpPlay) { window.cpPlay(card, 'pop'); window.cpPlay(card, 'shine'); }
}

function paint(root, state, cart) {
  // Rendered hidden on an empty cart; see cp-gift-ladder.liquid.
  root.hidden = !cart || !cart.item_count;
  paintShipping(root, cart);

  const headline = root.querySelector('[data-cp-ladder-headline]');
  // "bar", not "track": that name is the imported analytics helper, and
  // shadowing it threw "track is not a function" when the gift opened.
  const bar = root.querySelector('[data-cp-ladder-track]');
  const fill = root.querySelector('[data-cp-ladder-fill]');

  if (headline) headline.textContent = sentence(root, state);
  const giftRow = root.querySelector('[data-cp-goal-gift]');
  if (giftRow) {
    const on = !state.nextKind || state.nextKind === 'none';
    if (on && !giftRow.classList.contains('is-on') && window.cpPlay) { window.cpPlay(giftRow, 'pop'); window.cpPlay(giftRow, 'shine'); }
    giftRow.classList.toggle('is-on', on);
  }

  /*
   * A light passes along the bar when it grows — the "you got closer" moment
   * after an add, not on every redraw. The previous width lives on the
   * element so a re-render with the same cart stays still.
   */
  if (fill) {
    const before = parseFloat(fill.dataset.cpPct || '0');
    fill.style.width = state.fillPct + '%';
    fill.dataset.cpPct = String(state.fillPct);
    if (state.fillPct > before && before > 0 && window.cpPlay) {
      setTimeout(function () { window.cpPlay(fill, 'shine'); }, 450);
    }
  }
  if (bar) bar.setAttribute('aria-valuenow', String(Math.round(state.fillPct)));

  [1, 2, 3].forEach(function (level) {
    const on = state.unlocked.indexOf('T' + level) !== -1;
    const node = root.querySelector('[data-cp-node="' + level + '"]');
    const chip = root.querySelector('[data-cp-chip="' + level + '"]');
    const row = root.querySelector('[data-cp-level="' + level + '"]');

    if (node) node.classList.toggle('is-on', on);
    if (chip) chip.hidden = !on;
    if (row) row.classList.toggle('is-on', on);
  });

  const gift = root.querySelector('[data-cp-gift]');
  if (!gift) return;

  const hasGiftLine = (cart.items || []).some(function (item) {
    return item.properties && item.properties._gift === 'true';
  });
  const show = state.t2 && !hasGiftLine;

  /*
   * "Was it already open" lives on the element, not in a closure: the element
   * outlives this function but not the page, and the shake has to fire on the
   * transition only. A block that shakes on every cart redraw reads as a fault.
   */
  const wasOpen = root.dataset.cpGiftOpen === '1';

  gift.hidden = !show;
  root.dataset.hasGiftLine = hasGiftLine ? '1' : '0';
  root.dataset.cpGiftOpen = show ? '1' : '0';

  if (show && !wasOpen) {
    gift.classList.add('is-fresh');
    setTimeout(function () { gift.classList.remove('is-fresh'); }, 600);

    track('gift_tier_unlocked', { tier: 'T2', cart_value: Math.round(state.subtotal / 100), currency: 'UAH' });
  }
}

document.addEventListener(CP_LADDER_EVENT, function (event) {
  document.querySelectorAll('[data-cp-ladder]').forEach(function (root) {
    paint(root, event.detail.ladder, event.detail.cart);
  });
});

/* The levels list: reference material, folded away until asked for. */
document.addEventListener('click', function (event) {
  const toggle = event.target.closest('[data-cp-ladder-toggle]');
  if (!toggle) return;

  const root = toggle.closest('[data-cp-ladder]');
  const levels = root && root.querySelector('[data-cp-ladder-levels]');
  if (!levels) return;

  const opening = levels.hidden;
  levels.hidden = !opening;
  toggle.setAttribute('aria-expanded', String(opening));
  toggle.textContent = opening ? 'Згорнути' : 'Рівні';

  if (opening) track('tiers_expanded');
});

/*
 * Gift picker. One chip per variant rather than separate colour and size rows:
 * the gift is a single product with a handful of variants and Shopify already
 * names them ("Чорна / M"). Split this in two if the gift ever outgrows two
 * lines of chips.
 */
document.addEventListener('click', function (event) {
  const chip = event.target.closest('.cp-gift__chip');
  if (!chip || chip.disabled) return;

  const gift = chip.closest('[data-cp-gift]');
  gift.querySelectorAll('.cp-gift__chip').forEach(function (other) {
    other.setAttribute('aria-pressed', String(other === chip));
  });

  const add = gift.querySelector('[data-cp-gift-add]');
  if (add) add.disabled = false;
});

document.addEventListener('click', function (event) {
  const add = event.target.closest('[data-cp-gift-add]');
  if (!add || add.disabled) return;

  const gift = add.closest('[data-cp-gift]');
  const chosen = gift.querySelector('.cp-gift__chip[aria-pressed="true"]');
  if (!chosen) return;

  add.disabled = true;
  add.textContent = 'Додаємо…';

  enqueue(function () {
    return fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: Number(chosen.dataset.variantId),
            quantity: 1,
            /*
             * Shopify hides underscore-prefixed properties from the checkout
             * and the confirmation email: the customer sees a gift, not a pair
             * of internal flags.
             */
            properties: { _gift: 'true', _gift_tier: 'T2' },
          },
        ],
      }),
    })
      .then(function (response) { return response.json(); })
      .then(function () {
        add.textContent = 'Додати подарунок';

        track('gift_selected', { tier: 'T2', variant: chosen.textContent.trim() });

        /*
         * /cart/add.js answers with the added lines, not the cart, so the cart
         * is read back before announcing — the drawer redraws from that event
         * and the gift appears as a line instead of only moving the bar.
         */
        return fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { headers: { Accept: 'application/json' } })
          .then(function (response) { return response.json(); })
          .then(function (cart) { announce(cart, 'cp-gift-add'); });
      })
      .catch(function (error) {
        add.disabled = false;
        add.textContent = 'Додати подарунок';
        throw error;
      });
  });
});

/*
 * "Продовжити покупки" inside the drawer closes the drawer instead of
 * navigating: in an endless grid, a page load would throw away the scroll the
 * customer is coming back to. On the /cart page there is no drawer, so the
 * link stays a link.
 */
document.addEventListener('click', function (event) {
  const link = event.target.closest('[data-cp-continue]');
  if (!link) return;

  const drawer = link.closest('cart-drawer-component');
  if (!drawer || typeof drawer.close !== 'function') return;

  event.preventDefault();
  drawer.close();
});
